/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';

export type CollapsedErrorMessageProps = {
    /**
     * The error object.
     */
    error: Error;

    /**
     * Message to be displayed as a summery to the actual error.
     */
    collapsedErrorMessage?: string;

    /**
     * React elements to be rendered under the error message.
     */
    children?: React.ReactNode;
};

const messages = defineMessages({
    moreDetails: {
        id: 'core.collapsedErrorMessage.moreDetails',
        defaultMessage: 'more details.'
    },
    lessDetails: {
        id: 'core.collapsedErrorMessage.lessDetails',
        defaultMessage: 'less details.'
    }
});

const theme = getScopedTheme('CollapsedErrorMessage');
const errorIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { color: theme.errorIconColor, marginTop: '3px' } });
const errorTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { textOverflow: 'ellipsis', overflowX: 'hidden' }
});

/**
 * Represents a collapsed error message with a see more link to expand.
 */
export const CollapsedErrorMessage = (props: CollapsedErrorMessageProps) => {
    const { error, collapsedErrorMessage, children } = props;
    const { localize } = useLocalization();
    const [isExpanded, setIsExpanded] = React.useState(false);

    return (
        <Stack horizontal verticalAlign="start" tokens={{ childrenGap: 8 }}>
            <Icon iconName="ErrorBadge" styles={errorIconStyles} />
            <Stack.Item>
                {collapsedErrorMessage ? (
                    <LuisText data-automation-id="collapsedMessage" variant="smallPlus" styles={errorTextStyles}>
                        {collapsedErrorMessage}
                        {isExpanded && <Stack padding="16px 0 0 0">{error.message}</Stack>}
                        &nbsp;
                        <Link data-automation-id="detailsLink" onClick={() => setIsExpanded(val => !val)}>
                            {isExpanded ? localize(messages.lessDetails) : localize(messages.moreDetails)}
                        </Link>
                    </LuisText>
                ) : (
                    <LuisText data-automation-id="fullErrorMessage" variant="smallPlus" styles={errorTextStyles}>
                        {error.message}
                        &nbsp;
                    </LuisText>
                )}
                {children}
            </Stack.Item>
        </Stack>
    );
};
